// Pure timesheet-settings normalization + conversion helpers, extracted from
// EmployeeDetails.vue. They map raw server settings <-> the UI form shape <-> the
// server payload (with time normalization + range clamping). No Vue/axios — fully
// unit-testable.

export const DEFAULT_TIMESHEET_SETTINGS = {
  use_automatic: false,
  workday_start: "08:00",
  workday_end: "17:00",
  break_start: "12:00",
  break_end: "13:00",
  timezone: "",
  smart_tracker_enabled: false,
  smart_tracker_idle_seconds: 180,
  smart_tracker_prompt_seconds: 30,
  smart_tracker_auto_label_enabled: true,
};

export const DEFAULT_TIMESHEET_SERVER_TIMES = {
  workday_start: "08:00:00",
  workday_end: "17:00:00",
  break_start: "12:00:00",
  break_end: "13:00:00",
};

export const createDefaultTimesheetSettings = () => ({ ...DEFAULT_TIMESHEET_SETTINGS });

export const normalizeTimeForInput = (value, fallback) => {
  if (!value) return fallback;
  const str = String(value).trim();
  const match = str.match(/^(\d{2}):(\d{2})(?::\d{2})?/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }
  const parsed = new Date(`1970-01-01T${str}`);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().substring(11, 16);
  }
  return fallback;
};

export const ensureSeconds = (value, fallback) => {
  if (!value) return fallback;
  const str = String(value).trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(str)) {
    return str;
  }
  if (/^\d{2}:\d{2}$/.test(str)) {
    return `${str}:00`;
  }
  return fallback;
};

export const normalizeIntegerInRange = (value, fallback, min, max) => {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
};

export const normalizeDecimalInRange = (value, fallback, min, max, precision = 2) => {
  const normalized = String(value ?? "").replace(",", ".").trim();
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed)) return fallback;
  const clamped = Math.min(max, Math.max(min, parsed));
  return Number(clamped.toFixed(precision));
};

export const secondsToMinutes = (seconds, precision = 2) =>
  Number((Number(seconds || 0) / 60).toFixed(precision));

export const toUiTimesheetSettings = (raw = {}) => ({
  use_automatic: Boolean(raw.use_automatic),
  workday_start: normalizeTimeForInput(raw.workday_start, DEFAULT_TIMESHEET_SETTINGS.workday_start),
  workday_end: normalizeTimeForInput(raw.workday_end, DEFAULT_TIMESHEET_SETTINGS.workday_end),
  break_start: normalizeTimeForInput(raw.break_start, DEFAULT_TIMESHEET_SETTINGS.break_start),
  break_end: normalizeTimeForInput(raw.break_end, DEFAULT_TIMESHEET_SETTINGS.break_end),
  timezone: raw.timezone || "",
  smart_tracker_enabled: Boolean(raw.smart_tracker_enabled),
  smart_tracker_idle_seconds: normalizeIntegerInRange(
    raw.smart_tracker_idle_seconds,
    DEFAULT_TIMESHEET_SETTINGS.smart_tracker_idle_seconds,
    30,
    7200
  ),
  smart_tracker_prompt_seconds: normalizeIntegerInRange(
    raw.smart_tracker_prompt_seconds,
    DEFAULT_TIMESHEET_SETTINGS.smart_tracker_prompt_seconds,
    10,
    3600
  ),
  smart_tracker_auto_label_enabled:
    raw.smart_tracker_auto_label_enabled === null || raw.smart_tracker_auto_label_enabled === undefined
      ? DEFAULT_TIMESHEET_SETTINGS.smart_tracker_auto_label_enabled
      : Boolean(raw.smart_tracker_auto_label_enabled),
});

export const toServerTimesheetPayload = (settings) => ({
  use_automatic: Boolean(settings.use_automatic),
  workday_start: ensureSeconds(settings.workday_start, DEFAULT_TIMESHEET_SERVER_TIMES.workday_start),
  workday_end: ensureSeconds(settings.workday_end, DEFAULT_TIMESHEET_SERVER_TIMES.workday_end),
  break_start: ensureSeconds(settings.break_start, DEFAULT_TIMESHEET_SERVER_TIMES.break_start),
  break_end: ensureSeconds(settings.break_end, DEFAULT_TIMESHEET_SERVER_TIMES.break_end),
  timezone: settings.timezone || null,
  smart_tracker_enabled: Boolean(settings.smart_tracker_enabled),
  smart_tracker_idle_seconds: normalizeIntegerInRange(
    settings.smart_tracker_idle_seconds,
    DEFAULT_TIMESHEET_SETTINGS.smart_tracker_idle_seconds,
    30,
    7200
  ),
  smart_tracker_prompt_seconds: normalizeIntegerInRange(
    settings.smart_tracker_prompt_seconds,
    DEFAULT_TIMESHEET_SETTINGS.smart_tracker_prompt_seconds,
    10,
    3600
  ),
  smart_tracker_auto_label_enabled:
    settings.smart_tracker_auto_label_enabled === null ||
    settings.smart_tracker_auto_label_enabled === undefined
      ? DEFAULT_TIMESHEET_SETTINGS.smart_tracker_auto_label_enabled
      : Boolean(settings.smart_tracker_auto_label_enabled),
});
